/**
 * Auth Context for Y GO Shuttle Services France
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

// Helper to sync user language with i18n
const syncUserLanguage = (userLanguage) => {
  if (userLanguage && ['fr', 'en', 'de', 'es'].includes(userLanguage)) {
    // Dynamically import i18n to change language
    import('i18next').then((i18n) => {
      i18n.default.changeLanguage(userLanguage);
      localStorage.setItem('i18nextLng', userLanguage);
    });
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          // Verify token is still valid
          const userData = await authAPI.getMe();
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          // Sync user's language preference
          if (userData.language) {
            syncUserLanguage(userData.language);
          }
        } catch (err) {
          // Token expired or invalid
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const data = await authAPI.login(email, password);
      
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      setUser(data.user);
      
      // Sync user's language preference after login
      if (data.user?.language) {
        syncUserLanguage(data.user.language);
      }
      
      return data.user;
    } catch (err) {
      const message = err.response?.data?.detail || 'Erreur de connexion';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const register = useCallback(async (userData) => {
    setError(null);
    try {
      const data = await authAPI.register(userData);
      
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      setUser(data.user);
      return data.user;
    } catch (err) {
      const message = err.response?.data?.detail || "Erreur lors de l'inscription";
      setError(message);
      throw new Error(message);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } finally {
      setUser(null);
    }
  }, []);

  // Login with OTP - pour la connexion après vérification OTP
  const loginWithOTP = useCallback((token, userData, refreshToken = null) => {
    localStorage.setItem('access_token', token);
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    }
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    // Sync user's language preference
    if (userData?.language) {
      syncUserLanguage(userData.language);
    }
    
    return userData;
  }, []);

  const hasRole = useCallback((roleName) => {
    if (!user || !user.roles) return false;
    return user.roles.some(role => role.name === roleName);
  }, [user]);

  const hasPermission = useCallback((moduleId) => {
    if (!user) return false;
    // SUPERADMIN always has all permissions
    if (user.roles?.some(role => role.name === 'SUPERADMIN')) return true;
    // Check user's permissions array
    const perms = user.permissions;
    if (!perms || !Array.isArray(perms)) return false;
    return perms.includes(moduleId);
  }, [user]);

  const isSuperAdmin = useCallback(() => hasRole('SUPERADMIN'), [hasRole]);
  const isClient = useCallback(() => hasRole('CLIENT'), [hasRole]);
  const isChauffeur = useCallback(() => hasRole('CHAUFFEUR'), [hasRole]);

  const value = {
    user,
    loading,
    error,
    login,
    loginWithOTP,
    register,
    logout,
    hasRole,
    hasPermission,
    isSuperAdmin,
    isClient,
    isChauffeur,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
